import { useCallback, useRef, useState } from 'react';
import {
  loadStripeTerminal,
  type ConnectionStatus,
  type IPaymentIntent,
  type ISdkManagedPaymentIntent,
  type PaymentStatus,
  type Reader,
  type Terminal,
} from '@stripe/terminal-js';
import { trackFailure, trackOutcome } from '@/analytics/events';
import { formatUserFacingError } from '@/utils/userFacingError';
import {
  describeTerminalDecline,
  isRetriableTerminalDecline,
  isSuccessfulTerminalPaymentIntent,
  isTerminalUserCancellation,
  TerminalPaymentCancelledError,
} from '@/utils/terminalPayment';
import type {
  TerminalConnectionTokenResponse,
  TerminalReaderMetadata,
} from '@/services/revenue/functions';

type TerminalResultError = {
  error: {
    message?: string;
    code?: string;
  };
};

export type ReaderOption = TerminalReaderMetadata & {
  id: string;
  label: string;
  rawReader: Reader;
};

function isTerminalError(result: unknown): result is TerminalResultError {
  return Boolean(result && typeof result === 'object' && 'error' in result);
}

function formatReaderConnectError(error: TerminalResultError['error'], fallback: string) {
  const message = error.message?.trim() ?? '';
  const code = 'code' in error ? String((error as { code?: string }).code ?? '') : '';

  if (/in[_\s-]?use/i.test(message) || /in[_\s-]?use/i.test(code)) {
    return 'The card reader is in use by another checkout. Wait for the current payment to finish, then try again.';
  }

  return formatUserFacingError(error, fallback);
}

function summarizeReader(reader: Reader): ReaderOption {
  return {
    id: reader.id,
    readerId: reader.id,
    label: reader.label ?? reader.serial_number ?? reader.id,
    deviceType: reader.device_type,
    serialNumber: reader.serial_number,
    rawReader: reader,
  };
}

type TerminalPaymentPhase = 'idle' | 'collect' | 'process';

export type CollectAndProcessPaymentOptions = {
  tipEligibleAmountCents?: number;
  /** Called when a card is declined so staff can prompt another card without leaving checkout. */
  onPaymentDeclined?: (message: string) => void;
};

export function useStripeTerminalSession(
  fetchConnectionToken: () => Promise<TerminalConnectionTokenResponse>,
) {
  const terminalRef = useRef<Terminal | null>(null);
  const paymentPhaseRef = useRef<TerminalPaymentPhase>('idle');
  const intentionalDisconnectRef = useRef(false);
  const onUnexpectedDisconnectRef = useRef<(() => void) | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    'not_connected' as ConnectionStatus,
  );
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('not_ready' as PaymentStatus);
  const [readers, setReaders] = useState<ReaderOption[]>([]);
  const [connectedReader, setConnectedReader] = useState<ReaderOption | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const ensureTerminal = useCallback(async () => {
    if (terminalRef.current) {
      return terminalRef.current;
    }

    const stripeTerminal = await loadStripeTerminal();
    if (!stripeTerminal) {
      throw new Error('Stripe Terminal.js could not be loaded.');
    }

    terminalRef.current = stripeTerminal.create({
      onFetchConnectionToken: async () => {
        const token = await fetchConnectionToken();

        if (!token.secret) {
          throw new Error('Unable to create Stripe Terminal connection token.');
        }

        return token.secret;
      },
      onUnexpectedReaderDisconnect: () => {
        if (intentionalDisconnectRef.current) {
          intentionalDisconnectRef.current = false;
          return;
        }

        setConnectedReader(null);
        setLastError('Reader disconnected unexpectedly. Reconnecting…');
        trackOutcome('terminal_reader_disconnected', 'failed', {
          unexpected: true,
        });
        onUnexpectedDisconnectRef.current?.();
      },
      onConnectionStatusChange: (event) => setConnectionStatus(event.status),
      onPaymentStatusChange: (event) => setPaymentStatus(event.status),
    });

    return terminalRef.current;
  }, [fetchConnectionToken]);

  const registerUnexpectedDisconnectHandler = useCallback((handler: (() => void) | null) => {
    onUnexpectedDisconnectRef.current = handler;
  }, []);

  const discoverReaders = useCallback(
    async (config: { stripeLocationId: string }) => {
      setLastError(null);
      const terminal = await ensureTerminal();
      const result = await terminal.discoverReaders({
        method: 'internet',
        simulated: false,
        location: config.stripeLocationId,
      });

      if (isTerminalError(result)) {
        const message = formatUserFacingError(result.error, 'Unable to discover readers.');
        setLastError(message);
        trackFailure('terminal_reader_discovered', new Error(message), {
          stripe_location_id: config.stripeLocationId,
        });
        throw new Error(message);
      }

      const discoveredReaders = result.discoveredReaders.map(summarizeReader);
      setReaders(discoveredReaders);
      trackOutcome('terminal_reader_discovered', 'succeeded', {
        stripe_location_id: config.stripeLocationId,
        reader_count: discoveredReaders.length,
        device_types: discoveredReaders.map((reader) => reader.deviceType ?? 'unknown'),
      });

      return discoveredReaders;
    },
    [ensureTerminal],
  );

  const connectReader = useCallback(
    async (reader: ReaderOption) => {
      setLastError(null);
      const terminal = await ensureTerminal();
      const result = await terminal.connectReader(reader.rawReader, {
        fail_if_in_use: true,
      });

      if (isTerminalError(result)) {
        const message = formatReaderConnectError(result.error, 'Unable to connect reader.');
        setLastError(message);
        trackFailure('terminal_reader_connected', new Error(message), {
          reader_device_type: reader.deviceType,
          tap_to_pay: reader.tapToPay,
        });
        throw new Error(message);
      }

      const nextReader = summarizeReader(result.reader);
      setConnectedReader(nextReader);
      trackOutcome('terminal_reader_connected', 'succeeded', {
        reader_device_type: nextReader.deviceType,
        tap_to_pay: nextReader.tapToPay,
      });

      return nextReader;
    },
    [ensureTerminal],
  );

  const collectAndProcessPayment = useCallback(
    async (
      clientSecret: string,
      options?: CollectAndProcessPaymentOptions,
    ): Promise<IPaymentIntent> => {
      setLastError(null);
      const terminal = await ensureTerminal();

      const collectConfig = {
        config_override: {
          skip_tipping: false,
          tipping:
            typeof options?.tipEligibleAmountCents === 'number'
              ? {
                  eligible_amount: options.tipEligibleAmountCents,
                }
              : null,
        },
      };

      try {
        // Reuse the same PaymentIntent after declines (Stripe Terminal best practice).
        while (true) {
          paymentPhaseRef.current = 'collect';
          const collectResult = await terminal.collectPaymentMethod(clientSecret, collectConfig);

          if (isTerminalError(collectResult)) {
            if (isTerminalUserCancellation(collectResult.error)) {
              throw new TerminalPaymentCancelledError();
            }

            const message = formatUserFacingError(
              collectResult.error,
              'Unable to collect card-present payment.',
            );
            setLastError(message);
            trackFailure('terminal_payment_collected', new Error(message), {
              connection_status: connectionStatus,
              payment_status: paymentStatus,
            });
            throw new Error(message);
          }

          trackOutcome('terminal_payment_collected', 'succeeded', {
            connection_status: connectionStatus,
            payment_status: paymentStatus,
          });

          paymentPhaseRef.current = 'process';
          let processableIntent: ISdkManagedPaymentIntent = collectResult.paymentIntent;

          while (true) {
            const processResult = await terminal.processPayment(processableIntent);

            if (!isTerminalError(processResult)) {
              const paymentIntent: IPaymentIntent = processResult.paymentIntent;

              if (isSuccessfulTerminalPaymentIntent(paymentIntent)) {
                trackOutcome('terminal_payment_processed', 'succeeded', {
                  connection_status: connectionStatus,
                  payment_status: paymentStatus,
                });
                return paymentIntent;
              }

              if (paymentIntent.status === 'requires_confirmation') {
                processableIntent = paymentIntent as ISdkManagedPaymentIntent;
                continue;
              }

              if (isRetriableTerminalDecline(undefined, paymentIntent)) {
                const message = describeTerminalDecline(paymentIntent.last_payment_error);
                setLastError(message);
                trackOutcome('terminal_payment_processed', 'failed', {
                  connection_status: connectionStatus,
                  payment_status: paymentStatus,
                  retriable: true,
                });
                options?.onPaymentDeclined?.(message);
                break;
              }

              const message = 'Unable to process card-present payment.';
              setLastError(message);
              trackFailure('terminal_payment_processed', new Error(message), {
                connection_status: connectionStatus,
                payment_status: paymentStatus,
              });
              throw new Error(message);
            }

            if (isTerminalUserCancellation(processResult.error)) {
              throw new TerminalPaymentCancelledError();
            }

            if (isRetriableTerminalDecline(processResult.error, processableIntent)) {
              const message = describeTerminalDecline(processResult.error);
              setLastError(message);
              trackOutcome('terminal_payment_processed', 'failed', {
                connection_status: connectionStatus,
                payment_status: paymentStatus,
                retriable: true,
              });
              options?.onPaymentDeclined?.(message);
              break;
            }

            const message = formatUserFacingError(
              processResult.error,
              'Unable to process card-present payment.',
            );
            setLastError(message);
            trackFailure('terminal_payment_processed', new Error(message), {
              connection_status: connectionStatus,
              payment_status: paymentStatus,
            });
            throw new Error(message);
          }
        }
      } finally {
        paymentPhaseRef.current = 'idle';
      }
    },
    [connectionStatus, ensureTerminal, paymentStatus],
  );

  const cancelActivePayment = useCallback(async () => {
    const terminal = terminalRef.current;
    if (!terminal) {
      return;
    }

    const phase = paymentPhaseRef.current;
    if (phase === 'collect') {
      const result = await terminal.cancelCollectPaymentMethod();
      if (isTerminalError(result)) {
        throw new Error(formatUserFacingError(result.error, 'Unable to cancel card collection.'));
      }
      return;
    }

    if (phase === 'process') {
      const result = await terminal.cancelProcessPayment();
      if (isTerminalError(result)) {
        throw new Error(formatUserFacingError(result.error, 'Unable to cancel payment processing.'));
      }
    }
  }, []);

  const disconnectReader = useCallback(async () => {
    if (!terminalRef.current) {
      return;
    }

    intentionalDisconnectRef.current = true;
    await terminalRef.current.disconnectReader();
    setConnectedReader(null);
    trackOutcome('terminal_reader_disconnected', 'succeeded', {
      unexpected: false,
    });
  }, []);

  return {
    connectionStatus,
    paymentStatus,
    readers,
    connectedReader,
    lastError,
    discoverReaders,
    connectReader,
    collectAndProcessPayment,
    cancelActivePayment,
    disconnectReader,
    registerUnexpectedDisconnectHandler,
  };
}
